import { UserRole } from '../types';

export {};

// Define all available permissions (matching backend)
export const PERMISSIONS = {
  // Core permissions - everyone has these
  VIEW_HOME: 'view_home',
  VIEW_TIME: 'view_time',
  VIEW_PROJECTS: 'view_projects',
  VIEW_TEAM: 'view_team',
  VIEW_CHAT: 'view_chat',
  
  // Advanced permissions
  VIEW_EXPENSES: 'view_expenses',
  VIEW_REPORTS: 'view_reports',
  VIEW_INVOICES: 'view_invoices',
  VIEW_ESTIMATES: 'view_estimates',
  VIEW_MANAGE: 'view_manage',
  VIEW_AI_ANALYTICS: 'view_ai_analytics',
  VIEW_AI_SETTINGS: 'view_ai_settings',
  VIEW_SCREENSHOT_HISTORY: 'view_screenshot_history',
  
  // Testing permissions
  VIEW_TESTING: 'view_testing',
  MANAGE_TESTING: 'manage_testing',
  
  // Owner-only permissions
  VIEW_OWNER_DASHBOARD: 'view_owner_dashboard',
  MANAGE_TEAM_PERMISSIONS: 'manage_team_permissions',
  VIEW_TEAM_ANALYTICS: 'view_team_analytics',
  
  // Admin permissions
  MANAGE_USERS: 'manage_users',
  MANAGE_PROJECTS: 'manage_projects',
  MANAGE_SETTINGS: 'manage_settings',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Default permissions for each role
export const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  member: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_TIME,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_CHAT,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_AI_SETTINGS,
    PERMISSIONS.VIEW_SCREENSHOT_HISTORY,
  ],
  tester: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_TIME,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_CHAT,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_AI_SETTINGS,
    PERMISSIONS.VIEW_SCREENSHOT_HISTORY,
    PERMISSIONS.VIEW_TESTING,
    PERMISSIONS.MANAGE_TESTING,
  ],
  admin: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_TIME,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_CHAT,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_MANAGE,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_AI_SETTINGS,
    PERMISSIONS.VIEW_SCREENSHOT_HISTORY,
    PERMISSIONS.VIEW_TESTING,
    PERMISSIONS.MANAGE_TESTING,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_PROJECTS,
    PERMISSIONS.MANAGE_SETTINGS,
  ],
  administrator: [
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_TIME,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_CHAT,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_MANAGE,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_AI_SETTINGS,
    PERMISSIONS.VIEW_SCREENSHOT_HISTORY,
    PERMISSIONS.VIEW_TESTING,
    PERMISSIONS.MANAGE_TESTING,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_PROJECTS,
    PERMISSIONS.MANAGE_SETTINGS,
  ],
  owner: [
    // Owner has most permissions, but not Expenses/Invoices/Estimates (super_owner only)
    PERMISSIONS.VIEW_HOME,
    PERMISSIONS.VIEW_TIME,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_CHAT,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_MANAGE,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_AI_SETTINGS,
    PERMISSIONS.VIEW_SCREENSHOT_HISTORY,
    PERMISSIONS.VIEW_TESTING,
    PERMISSIONS.MANAGE_TESTING,
    PERMISSIONS.VIEW_OWNER_DASHBOARD,
    PERMISSIONS.MANAGE_TEAM_PERMISSIONS,
    PERMISSIONS.VIEW_TEAM_ANALYTICS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_PROJECTS,
    PERMISSIONS.MANAGE_SETTINGS,
  ],
};

export class PermissionsService {
  /**
   * Check if a user has a specific permission
   */
  static hasPermission(userPermissions: string[], permission: Permission): boolean {
    // Special case: "all" permission grants access to everything
    if (userPermissions.includes('all')) {
      return true;
    }
    return userPermissions.includes(permission);
  }

  /**
   * Check if a user has any of the specified permissions
   */
  static hasAnyPermission(userPermissions: string[], permissions: Permission[]): boolean {
    // Special case: "all" permission grants access to everything
    if (userPermissions.includes('all')) {
      return true;
    }
    return permissions.some(permission => userPermissions.includes(permission));
  }

  /**
   * Check if a user has all of the specified permissions
   */
  static hasAllPermissions(userPermissions: string[], permissions: Permission[]): boolean {
    // Special case: "all" permission grants access to everything
    if (userPermissions.includes('all')) {
      return true;
    }
    return permissions.every(permission => userPermissions.includes(permission));
  }

  /**
   * Get default permissions for a role
   */
  static getDefaultPermissions(role: UserRole): Permission[] {
    return DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.member;
  }

  /**
   * Get navigation items that a user can access based on their permissions
   */
  static getAccessibleNavigationItems(userPermissions: string[]): string[] {
    const navigationItems: { key: string; permission: Permission }[] = [
      { key: 'ai-analytics', permission: PERMISSIONS.VIEW_HOME },
      { key: 'time', permission: PERMISSIONS.VIEW_TIME },
      { key: 'projects', permission: PERMISSIONS.VIEW_PROJECTS },
      { key: 'team', permission: PERMISSIONS.VIEW_TEAM },
      { key: 'expenses', permission: PERMISSIONS.VIEW_EXPENSES },
      { key: 'reports', permission: PERMISSIONS.VIEW_REPORTS },
      { key: 'invoices', permission: PERMISSIONS.VIEW_INVOICES },
      { key: 'estimates', permission: PERMISSIONS.VIEW_ESTIMATES },
      { key: 'manage', permission: PERMISSIONS.VIEW_MANAGE },
      { key: 'testing', permission: PERMISSIONS.VIEW_TESTING },
    ];

    return navigationItems
      .filter(item => this.hasPermission(userPermissions, item.permission))
      .map(item => item.key);
  }

  /**
   * Check if user can access owner dashboard
   */
  static canAccessOwnerDashboard(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, PERMISSIONS.VIEW_OWNER_DASHBOARD);
  }

  /**
   * Check if user can manage team permissions
   */
  static canManageTeamPermissions(userPermissions: string[]): boolean {
    return this.hasPermission(userPermissions, PERMISSIONS.MANAGE_TEAM_PERMISSIONS);
  }

  /**
   * Get role display name
   */
  static getRoleDisplayName(role: UserRole): string {
    console.log('🏷️ PermissionsService getRoleDisplayName called with role:', role, 'type:', typeof role);
    const normalizedRole = role.toLowerCase();
    console.log('🏷️ PermissionsService normalized role:', normalizedRole);
    
    switch (normalizedRole) {
      case 'owner':
        return 'Owner';
      case 'administrator':
      case 'admin':
        return 'Administrator';
      case 'tester':
        return 'Tester';
      case 'member':
        return 'Member';
      default:
        console.log('⚠️ PermissionsService unknown role, using Member as default:', role);
        return 'Member';
    }
  }

  /**
   * Get role color for UI
   */
  static getRoleColor(role: UserRole): string {
    switch (role) {
      case 'owner':
        return 'text-purple-600 bg-purple-100';
      case 'administrator':
        return 'text-blue-600 bg-blue-100';
      case 'tester':
        return 'text-orange-600 bg-orange-100';
      case 'member':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }
}
